import React, { useState, useEffect } from 'react'
import { pick } from 'lodash-es'
import { useTbViewStore, useFetchRemoteData } from '@vt/hooks'
import { IconMaps } from '@ow/icons'
import { File, ButtonGroup } from '@ow/comps'
import clsx from 'clsx'
import { BlockRender, InlineRender, OtherArticle } from '@ow/ditto'
import { translateTwoLanguages } from '@vt/apis/lib-ex/v2/translate'
import { Skeleton } from '@mantine/core'
import { StyledArticleDetail } from '../styled/detail'
import useArticleDetail from './use-detail'
import Author from './author'

const RenderImg = function ({ imageSlug, detailData, fields }) {
  return (
    imageSlug &&
    detailData[imageSlug] && (
      <div className="article-detail-images">
        <BlockRender
          className="img-content"
          rowData={detailData}
          value={detailData[imageSlug]}
          fields={fields}
          defs={fields.find((v) => v.slug === imageSlug)}
        />
      </div>
    )
  )
}

const Detail = function ({ detailData = {} }) {
  const {
    isShowDetailTop,
    isShowDetailRight,
    imagePosition,
    titleSlug,
    summarySlug,
    createBySlug,
    dateSlug,
    imageSlug,
    fileSlug,
    link,
    bottomField_main = [],
    isShowSide,
    isShowTranslate,
    toThousands,
  } = useArticleDetail()
  const { fields, tbId, articleConfig } = useTbViewStore((state) =>
    pick(state, ['fields', 'tbId', 'articleConfig'])
  )
  const fetchRemoteData = useFetchRemoteData()
  /** 图片位置如果不是在内容上面，则默认在内容底部 */
  const imgIsTop = imagePosition === 'top'

  const [activeKey, setActiveKey] = useState(0)
  const [transDetailData, setDetailData] = useState({})
  useEffect(() => {
    setDetailData(detailData)
    setActiveKey(0)
  }, [detailData])

  // 切换原文/中文
  const btnGroupOptions = [
    { key: 0, label: '原文' },
    { key: 1, label: '中文' },
  ]

  // 是否要显示翻译loading
  const isOpenTransformLoading = activeKey === 1 && !transDetailData.chinese

  // 切换中文/英文
  const transformLanguage = async (e) => {
    setActiveKey(e)
    // 切换中文 并且 没有翻译过中文
    if (e === 1 && !transDetailData.chinese) {
      let summary_ch
      let title_ch
      // 文章内容
      if (summarySlug) {
        summary_ch = await translateTwoLanguages(
          'en',
          'zh',
          detailData[summarySlug]
        )
      }
      // 文章标题
      if (titleSlug) {
        title_ch = await translateTwoLanguages(
          'en',
          'zh',
          detailData[titleSlug]
        )
      }
      setDetailData({
        ...transDetailData,
        chinese: {
          [summarySlug]: summary_ch || '',
          [titleSlug]: title_ch || '',
        },
      })
    }
  }

  const getSummaryContent = () => {
    if (activeKey === 0) {
      return transDetailData[summarySlug]
    }
    return transDetailData.chinese && transDetailData.chinese[summarySlug]
  }

  return (
    <div className="flex-1 overflow-auto">
      <StyledArticleDetail>
        <div className="detail-box flex item-center gap-10">
          <div
            className="detail-left"
            style={{ width: !isShowDetailRight && '100%' }}
          >
            {isShowDetailTop && (
              <div className="top-info border-neutral-300 dark:border-darkTheme-900">
                {titleSlug && (
                  <InlineRender
                    className="detail-title text-gray-100 dark:text-dark-200"
                    value={
                      activeKey === 0
                        ? transDetailData[titleSlug]
                        : transDetailData.chinese &&
                          transDetailData.chinese[titleSlug]
                    }
                    fields={fields}
                    defs={fields.find((v) => v.slug === titleSlug)}
                  />
                )}
                {titleSlug && isOpenTransformLoading && (
                  <Skeleton height={8} mt={6} mb={6} width="70%" radius="xl" />
                )}
                <div className="flex justify-between items-center mb-4 flex-wrap">
                  <div className="info-box">
                    {createBySlug && (
                      <InlineRender
                        className="article-author"
                        value={detailData[createBySlug]}
                        fields={fields}
                        defs={fields.find((v) => v.slug === createBySlug)}
                      />
                    )}
                    {dateSlug && (
                      <InlineRender
                        className="article-date text-themeNeutral-700 dark:text-dark-300"
                        value={detailData[dateSlug]}
                        fields={fields}
                        defs={fields.find((v) => v.slug === dateSlug)}
                      />
                    )}
                    {link && (
                      <div
                        className="flex items-center"
                        onClick={() => {
                          window.open(detailData[link.slug], `_${link.target}`)
                        }}
                      >
                        <span className="article-link-title text-themeNeutral-700 dark:text-dark-300">
                          {link.title}：
                        </span>
                        <InlineRender
                          className="article-link text-themeNeutral-700 dark:text-dark-300"
                          value={detailData[link.slug]}
                          fields={fields}
                          defs={fields.find((v) => v.slug === link.slug)}
                        />
                      </div>
                    )}
                    {bottomField_main.map((field) => {
                      return (
                        <div className="flex items-center">
                          {field.icon && (
                            <div>
                              <IconMaps
                                width={16}
                                height={16}
                                type={field.icon}
                                fill={field.iconFill}
                              />
                            </div>
                          )}
                          <div
                            className={clsx([
                              'flex',
                              'items-center',
                              field.titlePosition &&
                                field.titlePosition === 'left' &&
                                'flex-row-reverse',
                            ])}
                          >
                            <InlineRender
                              className="number text-themeNeutral-700 dark:text-dark-300"
                              value={
                                field.type === 'number'
                                  ? toThousands(detailData[field.slug])
                                  : detailData[field.slug]
                              }
                              fields={fields}
                              defs={fields.find((v) => v.slug === field.slug)}
                            />
                            {field.showTitle && (
                              <span className="text text-themeNeutral-700 dark:text-dark-300">
                                {field.title}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {isShowTranslate && (
                    <ButtonGroup
                      onClick={(e) => {
                        transformLanguage(e)
                      }}
                      options={btnGroupOptions}
                      activeType="primary"
                      borderColor="var(--ow-color-neutral-400)"
                      textColor="var(--ow-color-neutral-700)"
                      activeKey={activeKey}
                      borderRadius={4}
                    />
                  )}
                </div>
              </div>
            )}
            {imgIsTop && (
              <RenderImg
                imageSlug={imageSlug}
                detailData={transDetailData}
                fields={fields}
              />
            )}
            {summarySlug && (
              /** 使用File组件，可以将富文本编辑器的内容渲染出来
               * 如果值是数组，则是富文本编辑器内容，传给shortConSlateRichText
               * 如果值不是数组，则是纯文本，传给content
               */
              <File
                style={{ padding: 0 }}
                shortConSlateRichText={
                  Array.isArray(getSummaryContent())
                    ? getSummaryContent()
                    : null
                }
                content={
                  Array.isArray(getSummaryContent())
                    ? null
                    : getSummaryContent()
                }
                type="list"
              />
            )}
            {summarySlug && isOpenTransformLoading && (
              <Skeleton height={8} mt={6} mb={6} width="70%" radius="xl" />
            )}
            {fileSlug && (
              <InlineRender
                // className="detail-title"
                value={transDetailData[fileSlug]}
                fields={fields}
                rowData={transDetailData}
                defs={fields.find((v) => v.slug === fileSlug)}
              />
            )}
            {!imgIsTop && (
              <RenderImg
                className="article-detail-images"
                imageSlug={imageSlug}
                detailData={transDetailData}
                fields={fields}
              />
            )}
          </div>
          {isShowDetailRight && (
            <div className="detail-right flex flex-col">
              <Author detailData={transDetailData} />
              {isShowSide && (
                <div className="other-article flex-1">
                  <OtherArticle
                    articleConfig={articleConfig}
                    username={transDetailData.username}
                    tbId={tbId}
                    fetchRemoteData={fetchRemoteData}
                    tbFields={fields}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </StyledArticleDetail>
    </div>
  )
}
export default Detail
